import { useAuth } from "@mindlercare/auth-ui";
import { useFlag } from "@mindlercare/unleash-client";
import { useMindlerApi } from "api/apiv2";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useMutation } = ReactQueryV5;
import { useGetPsychologistIcbtCommunication } from "api/useIcbtQueries";
import { getLocale, locales } from "components/i18nResources";
import { PsychologistDropdown } from "components/lib/PsychologistDropdown";
import Text from "components/lib/text";
import { Typography } from "components/typography";
import { useDemo } from "contexts/DemoContext";
import CONSTANTS from "core/constants";
import { useCurrentMarketCode } from "core/selectors/marketSelectors";
import { TemplateNames } from "core/types";
import {
  format as dateFnsFormat,
  fromUnixTime,
  getDay,
  getUnixTime,
  parse,
  startOfWeek,
  isBefore,
} from "date-fns";
import { getTemplate, getTemplateName } from "helpers/templates";
import { usePsychologistUser } from "hooks";
import { useUserHasRole } from "hooks/userHooks";
import { useSelectedTemplate } from "hooks/useSelectedTemplate";
import { useSlotDialogParam } from "hooks/useSlotDialogParam";
import { useTemplates } from "hooks/useTemplates";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { useTranslation } from "react-i18next";

import { Button, Icon } from "@mindlercare/ui-web";
import ConfirmDialog from "../dialogs/confirmDialog";
import {
  AlertDialog,
  AlertDialogInnerContainer,
  Container,
  EventPatientTitle,
  EventTitle,
  ActionButton,
  CalendarContainer,
  Icon as IconWrapper,
} from "./booking.styles";
import { customCalendarComponents } from "./customCalendarComponents";
import { dayLayoutAlgorithm } from "./DayLayoutAlgorithm";
import { GlobalCalendarStyles } from "./globalStyles";
import { useWeekDate } from "./hooks/useWeekDate";
import { styled } from "styled-components";
import { useSelectedPsychologist } from "./hooks/useSelectedPsychologist";
import { PsychologistInfoDialog } from "./PsychologistInfoDialog";
import { useCalendarStartTime } from "./hooks/useCalendarStartTime";
import { useSlots } from "./hooks/useSlots/useSlots";
import { useAlertContext } from "./AlertContext";
import { useHandleEventDrop } from "./hooks/useHandleEventDrop";
import { useHandleEventResize } from "./hooks/useHandleEventResize";
import { useHandleCreateSlot } from "./hooks/useHandleCreateSlot";
import { useServiceWindows, serviceWindowAt } from "./hooks/useServiceWindows";
import { useInvalidateSlots } from "./hooks/useInvalidateSlots";
import { eventDragStartStore } from "./hooks/eventDragStartStore";

// Transform to custom format that is not default by the locales
const formatTransformer = (date, defaultFormat, options) => {
  let customFormat = defaultFormat;
  if (defaultFormat === "DD") {
    customFormat = "dd";
  }
  if (defaultFormat === "ddd") {
    customFormat = "eee";
  }
  return dateFnsFormat(date, customFormat, options);
};
const localizer = dateFnsLocalizer({
  format: formatTransformer,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const DnDCalendar = withDragAndDrop(Calendar);
export const Booking = () => {
  const { t } = useTranslation();

  const currentMarket = useCurrentMarketCode();
  const { data: templates } = useTemplates();
  const [currentTemplate] = useSelectedTemplate();

  const isAdmin = useUserHasRole("Admin");
  const isDemo = useDemo();
  const slots = useSlots({ isAdmin, isDemo });

  const [, setSlotIdParam] = useSlotDialogParam();

  const { userId } = useAuth();
  const selectedPsychologist = useSelectedPsychologist();
  const selectedPsychologistUserId = selectedPsychologist?.user.userId;

  const { isLoading, psychologist } = usePsychologistUser();

  const { date, setWeekDate } = useWeekDate();

  const { data: serviceWindows = [] } = useServiceWindows();

  const { alertDialog, clearAlert, showAlert } = useAlertContext();

  const [eventDrop, setEventDrop] = useState(null);
  const [eventDragStart, setEventDragStart] = useState(null);
  const [modalConfirm, setModalConfirm] = useState(null);

  const eventDragStartRef = useRef(eventDragStart);

  useEffect(() => {
    eventDragStartRef.current = eventDragStart;
    eventDragStartStore.set(eventDragStart);
  }, [eventDragStart]);

  const [timeRangeMin] = useCalendarStartTime();

  const invalidateSlots = useInvalidateSlots();
  const onEventDrop = useHandleEventDrop({ setEventDrop, eventDragStartRef });
  const onEventResize = useHandleEventResize({
    setEventDrop,
    eventDragStartRef,
  });
  const onCreateSlot = useHandleCreateSlot();

  const icbtCommunicationFeaturesEnabled = useFlag(
    "ICBT_COMMUNICATION_MC_FEATURES",
  );

  const { data: icbtCommunicationAndSlots } =
    useGetPsychologistIcbtCommunication(undefined, {
      enabled: icbtCommunicationFeaturesEnabled && !!psychologist,
    });

  const icbtCommunication = icbtCommunicationAndSlots?.icbtCommunications;

  const checkIsBefore = useCallback(
    (slot, isSelectedTemplate = true) => {
      const start = fromUnixTime(slot.startsAt);
      const gotTemplate = getTemplate(slot.templateId, templates);

      if (isBefore(start, new Date()) && !gotTemplate.allowInPast) {
        if (!isSelectedTemplate) {
          showAlert(t("PLEASE_SELECT_TEMPLATE_FIRST"));
        } else {
          showAlert(t("BOOK_SLOT_IN_THE_PAST"));
        }
        invalidateSlots();
        return false;
      }
      return true;
    },
    [t, templates, showAlert, invalidateSlots],
  );

  const eventIsOverlappingWithSlot = (event, slot, currentTemplates) => {
    const allowedOverlapTemplates = [TemplateNames.work_hours];

    const eventTemplate = getTemplate(event.templateId, currentTemplates);
    const slotTemplate = getTemplate(slot.templateId, currentTemplates);

    const isOverlapAllowed =
      allowedOverlapTemplates.includes(slotTemplate?.name) ||
      allowedOverlapTemplates.includes(eventTemplate?.name);

    if (isOverlapAllowed) {
      return false;
    }

    return (
      new Date(slot.startsAt) < new Date(event.endsAt) &&
      new Date(slot.endsAt) > new Date(event.startsAt)
    );
  };

  const isOverlapping = (event, currentSlots, currentTemplates) => {
    const ignoreEventId = event?.event?.slotId;
    const slotsToCheck = ignoreEventId
      ? currentSlots.filter((slot) => slot.slotId !== ignoreEventId)
      : currentSlots;
    for (let i = 0; i < slotsToCheck.length; i += 1) {
      const gotTemplate = getTemplate(
        slotsToCheck[i].templateId,
        currentTemplates,
      );
      if (
        !gotTemplate.allowParallel &&
        eventIsOverlappingWithSlot(event, slotsToCheck[i], currentTemplates)
      ) {
        if (
          slotsToCheck[i].status !== CONSTANTS.BOOKING_STATUS.CANCELED &&
          slotsToCheck[i].status !== CONSTANTS.BOOKING_STATUS.LATE_CANCELED
        ) {
          return true;
        }
      }
    }
    return false;
  };

  const psychologistUserId = selectedPsychologistUserId ?? userId;

  const api = useMindlerApi();

  const { mutateAsync: createSlot } = useMutation(
    api.slots.psychologist.create.mutationOptions(),
  );

  const { mutateAsync: moveSlot } = useMutation(
    api.slots.psychologist.move.mutationOptions(),
  );

  const createSlotFunction = useCallback(
    async (slot) => {
      onCreateSlot(
        psychologistUserId,
        slot,
        t,
        createSlot,
        templates,
        currentTemplate,
      );
    },
    [
      t,
      psychologistUserId,
      createSlot,
      templates,
      currentTemplate,
      onCreateSlot,
    ],
  );

  const onSelectEvent = useCallback(
    async (event) => {
      if (event?.slotId) {
        await setSlotIdParam(event.slotId);
      }
    },
    [setSlotIdParam],
  );

  const updateEvent = useCallback(async () => {
    if (!eventDrop) {
      return;
    }

    const { event, start, end } = eventDrop;

    setModalConfirm(null);
    setEventDragStart(null);
    setEventDrop(null);

    try {
      await moveSlot({
        psychologistUserId,
        startsAt: start,
        endsAt: end,
        slotId: event.slotId,
      });
    } catch (err) {
      if (err?.errorLangKey) {
        showAlert(t(err.errorLangKey));
      }
    } finally {
      await invalidateSlots();
    }
  }, [eventDrop, moveSlot, psychologistUserId, invalidateSlots, showAlert, t]);

  // Run check on dropped event
  useEffect(() => {
    const fetchData = async () => {
      const gotTemplate = await getTemplate(
        eventDrop.event.templateId,
        templates,
      );

      if (!checkIsBefore(eventDrop?.event)) {
        return;
      }

      if (
        (!isOverlapping(eventDrop, slots, templates) &&
          eventDrop.event.status === "BOOKED") ||
        (gotTemplate.allowParallel && eventDrop.event.status === "BOOKED")
      ) {
        setModalConfirm(true);
      }
      if (
        eventDrop.event.status === "OPEN" &&
        !isOverlapping(eventDrop, slots, templates)
      ) {
        updateEvent();
      }
    };

    if (eventDrop?.event) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDrop, templates, checkIsBefore, updateEvent]);

  const checkEventStatus = (event) => {
    if (event && event.status) {
      const status = event.status.toUpperCase();
      if (status === CONSTANTS.BOOKING_STATUS.OPEN) {
        return "";
      }
      return t(status, status);
    }
    return null;
  };

  const getEventTitle = (event) => {
    const template = templates.find((i) => i.templateId === event.templateId);
    const templateName =
      template && template.name ? (
        <span>{event?.bookingName || getTemplateName(template.name, t)}</span>
      ) : (
        ""
      );

    const patientName = (
      <EventPatientTitle>
        <Text sensitiveField={true} value={event.firstName} />
        <Text sensitiveField={true} value={event.lastName} />
      </EventPatientTitle>
    );
    return (
      <EventTitle>
        {templateName}
        {patientName}
        {checkEventStatus(event)}
      </EventTitle>
    );
  };

  const getSlotColor = (templateId) => {
    for (let i = 0; i < templates.length; i += 1) {
      if (templateId === templates[i].templateId) {
        return templates[i].color;
      }
    }

    return "red";
  };

  const eventPropGetter = (event, start, end) => {
    const endTime = new Date(end);
    if (isBefore(endTime, new Date())) {
      return {
        className: `pastSlot slotId-${event.slotId}`,
      };
    }
    return {
      style: {
        backgroundColor: getSlotColor(event.templateId),
        color: "#808080",
      },
      className: `slotId-${event.slotId}`,
    };
  };

  const cancelSlotComfirmation = useCallback(async () => {
    await invalidateSlots();
    setModalConfirm(null);
    setEventDragStart(null);
  }, [invalidateSlots]);

  const formats = useMemo(() => {
    return {
      dateFormat: CONSTANTS.DATE_FORMAT.DAY_DAY,
      weekdayFormat: CONSTANTS.DATE_FORMAT.DATE_NUMBER,
      timeGutterFormat: CONSTANTS.DEFAULT_TIME_FORMAT,
    };
  }, []);

  const slotIsResizable = useCallback(
    (slot) => {
      const template = getTemplate(slot.templateId, templates);
      if (
        currentMarket !== CONSTANTS.NL &&
        template?.name === CONSTANTS.NEW_VISIT
      ) {
        return false;
      }
      return template?.allowCustomLength;
    },
    [templates, currentMarket],
  );

  const checkSlotIdBeforeDrag = useCallback((slot) => {
    if (!slot?.slotId) return false;
    return true;
  }, []);

  const calendarComponents = useMemo(
    () =>
      customCalendarComponents({
        slots,
        icbtCommunication,
        icbtCommunicationFeaturesEnabled,
      }),
    [slots, icbtCommunication, icbtCommunicationFeaturesEnabled],
  );

  const startAccessor = useCallback(
    (event) => fromUnixTime(event.startsAt),
    [],
  );
  const endAccessor = useCallback((event) => fromUnixTime(event.endsAt), []);

  const onEventDropHandler = useCallback(
    (props) => onEventDrop(props, templates),
    [onEventDrop, templates],
  );
  const onEventResizeHandler = useCallback(
    (props) => onEventResize(props, templates),
    [onEventResize, templates],
  );

  const slotPropGetterCb = useCallback(
    (d) => {
      const window = serviceWindowAt(serviceWindows, getUnixTime(d));
      return {
        "data-date": dateFnsFormat(d, "yyyy-MM-dd"),
        "data-time": dateFnsFormat(d, "HH:mm"),
        ...(window && {
          className: "serviceWindowSlot",
          title: window.reason
            ? t("SERVICE_WINDOW_CALENDAR_TOOLTIP_WITH_REASON", {
                reason: window.reason,
              })
            : t("SERVICE_WINDOW_CALENDAR_TOOLTIP"),
        }),
      };
    },
    [serviceWindows, t],
  );

  const onDragStartCb = useCallback(
    (props) => setEventDragStart(props),
    [setEventDragStart],
  );

  return (
    <Container>
      <GlobalCalendarStyles />
      {!!alertDialog?.text && (
        <AlertDialog
          PaperProps={{
            style: {
              borderRadius: 0,
            },
          }}
          onClose={clearAlert}
          open={!!alertDialog?.text}
        >
          <AlertDialogInnerContainer>
            {alertDialog?.icon === "checkmark" && (
              <IconWrapper>
                <Icon.Done />
              </IconWrapper>
            )}
            {alertDialog?.icon === "warning" && (
              <IconWrapper>
                <Icon.Warning />
              </IconWrapper>
            )}
            <Typography
              dataTestId="play-alert-dialog-text"
              variant="h3"
              sx={{ paddingBottom: "24px" }}
            >
              {alertDialog.text}
            </Typography>
            <Button
              onClick={clearAlert}
              as={ActionButton}
              testId={"cy-ok-button"}
              variant="primary"
            >
              {t("OK")}
            </Button>
          </AlertDialogInnerContainer>
        </AlertDialog>
      )}
      {isAdmin && !isLoading && (
        <FlexHorizontal>
          <PsychologistDropdown />
          <PsychologistInfoDialog />
        </FlexHorizontal>
      )}
      <CalendarContainer>
        <DnDCalendar
          style={{ lineHeight: 0 }}
          eventOverlap={false}
          showMultiDayTimes={true}
          components={calendarComponents}
          startAccessor={startAccessor}
          endAccessor={endAccessor}
          draggableAccessor={(slot) => checkSlotIdBeforeDrag(slot)}
          titleAccessor={getEventTitle}
          formats={formats}
          step={5}
          timeslots={12}
          views={["week"]}
          date={date}
          min={timeRangeMin}
          localizer={localizer}
          culture={getLocale()}
          defaultDate={new Date()}
          defaultView="week"
          events={slots}
          onEventDrop={onEventDropHandler}
          onEventResize={onEventResizeHandler}
          eventPropGetter={eventPropGetter}
          slotPropGetter={slotPropGetterCb}
          eventDurationEditable={false}
          selectable={true}
          onNavigate={(d) => setWeekDate(d)}
          onSelectSlot={createSlotFunction}
          onSelectEvent={onSelectEvent}
          onDragStart={onDragStartCb}
          dayLayoutAlgorithm={dayLayoutAlgorithm}
          resizableAccessor={slotIsResizable}
        />
      </CalendarContainer>
      <ConfirmDialog
        modalConfirm={!!modalConfirm}
        confirmSlot={t("CONFIRM_SLOT")}
        cancelDial={cancelSlotComfirmation}
        cancel={t("CANCEL")}
        confirmDial={updateEvent}
        confirm={t("CONFIRM")}
      />
    </Container>
  );
};

const FlexHorizontal = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;
